/**
 * Subtask View Page - Opens a subtask modal directly from a shareable link
 */
import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SubtaskModal from '@/components/SubtaskModal/SubtaskModal';
import { subtaskAPI } from '@/api/subtaskAPI';
import { taskAPI } from '@/api/taskAPI';
import { commentAPI } from '@/api/commentAPI';
import { projectMemberAPI } from '@/api/projectMemberAPI';
import { mastersAPI, WorkflowStatus, Priority } from '@/api/mastersApi';
import { Loader } from '@/components/Loader';
import { Subtask, Task } from '@/types';
import { Comment } from '@/types/comments';
import { deleteComment, updateComment } from '@/utils/commentUtils';
import toast from 'react-hot-toast';
import { projectAPI } from '@/api/projectAPI';
import { buildTaskShareTitle, formatTaskIdentifier } from '@/utils/documentTitle';

export const SubtaskView = () => {
  const { subtaskId } = useParams<{ subtaskId: string }>();
  const navigate = useNavigate();

  const [subtask, setSubtask] = useState<Subtask | null>(null);
  const [parentTask, setParentTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [projectMembers, setProjectMembers] = useState<any[]>([]);
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatus[]>([]);
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [activity, setActivity] = useState<any[]>([]);
  const [projectKey, setProjectKey] = useState<string>('AHM');
  const hasLoadedRef = useRef(false);

  // Reset hasLoadedRef when subtaskId changes
  useEffect(() => {
    hasLoadedRef.current = false;
  }, [subtaskId]);

  useEffect(() => {
    if (!subtask) {
      return;
    }

    const derivedProjectKey =
      projectKey ||
      (parentTask as any)?.project_key ||
      (parentTask as any)?.projectKey ||
      (parentTask as any)?.project?.key ||
      (subtask as any)?.project_key ||
      null;

    const parentIdentifier = parentTask
      ? formatTaskIdentifier({
          projectKey: derivedProjectKey,
          displayNumber: (parentTask as any)?.display_number ?? null,
          fallbackId:
            (parentTask as any)?.task_key ||
            (parentTask as any)?.code ||
            (parentTask as any)?.display_code ||
            null,
          taskId: parentTask.id,
        })
      : derivedProjectKey || null;

    const subDisplay =
      (subtask as any)?.display_number ??
      (subtask as any)?.displayNumber ??
      (subtask as any)?.number ??
      null;

    const fallbackId = parentIdentifier
      ? subDisplay
        ? `${parentIdentifier}.${subDisplay}`
        : parentIdentifier
      : subDisplay
        ? `${subDisplay}`
        : null;

    document.title = buildTaskShareTitle({
      fallbackId: fallbackId || `Subtask ${subtask.id.substring(0, 6)}`,
      taskId: subtask.id,
      title: subtask.title,
    });
  }, [subtask, parentTask, projectKey]);

  // Load subtask data - only once
  useEffect(() => {
    if (!subtaskId || hasLoadedRef.current) {
      if (!subtaskId) {
        setError('Subtask ID is required');
        setLoading(false);
      }
      return;
    }

    const loadSubtask = async () => {
      try {
        hasLoadedRef.current = true;
        setLoading(true);
        setError(null); // Clear any previous errors
        
        // First, try to load the subtask
        const subtaskData = await subtaskAPI.get(subtaskId);
        
        if (!subtaskData) {
          throw new Error('Subtask not found');
        }
        
        setSubtask(subtaskData);

        // Load parent task (non-blocking - don't fail if parent task can't be loaded)
        const taskId = (subtaskData as any).task_id;
        let parentProjectId: string | null = null;
        if (taskId) {
          try {
            const parentTaskData = await taskAPI.getTask(taskId);
            setParentTask(parentTaskData);
            parentProjectId = parentTaskData.project_id;
            
            // Fetch project to get project key (non-blocking)
            if (parentProjectId) {
              projectAPI.getProject(parentProjectId).then((project) => {
                if (project?.key) {
                  setProjectKey(project.key);
                }
              }).catch((err) => {
                console.error('Failed to fetch project:', err);
              });
            }
          } catch (err: any) {
            // Parent task load failure is not critical - log but don't fail
            console.error('Failed to load parent task:', err);
            // Try to get project_id from subtask if available
            const subtaskProjectId = (subtaskData as any).project_id;
            if (subtaskProjectId) {
              parentProjectId = subtaskProjectId;
            }
          }
        }

        // Load related data (non-blocking - use empty arrays if fails)
        const [members, statuses, prios, commentsData] = await Promise.all([
          parentProjectId ? projectMemberAPI.getProjectMembers(parentProjectId).catch(() => []) : Promise.resolve([]),
          mastersAPI.getWorkflowStatuses().catch(() => []),
          mastersAPI.getPriorities().catch(() => []),
          commentAPI.getComments(subtaskId).catch(() => []),
        ]);

        setProjectMembers(members || []);
        setWorkflowStatuses((statuses || []).sort((a, b) => (a.order || 0) - (b.order || 0)));
        setPriorities(prios || []);
        setComments(commentsData || []);
        setActivity(commentsData || []);

        // Open modal once subtask data is loaded (even if parent task failed)
        setIsModalOpen(true);
      } catch (err: any) {
        console.error('Failed to load subtask:', err);
        // Extract error message - handle different error formats
        let errorMessage = 'Failed to load subtask';
        if (err?.response?.data?.detail) {
          errorMessage = err.response.data.detail;
        } else if (err?.message) {
          errorMessage = err.message;
        } else if (typeof err === 'string') {
          errorMessage = err;
        }
        
        // If the error mentions "Task not found" but we're loading a subtask, clarify the message
        if (errorMessage.toLowerCase().includes('task not found') && !errorMessage.toLowerCase().includes('subtask')) {
          errorMessage = 'Subtask not found (or parent task not accessible)';
        }
        
        setError(errorMessage);
        setSubtask(null); // Ensure subtask is null on error
        hasLoadedRef.current = false; // Allow retry on error
      } finally {
        setLoading(false);
      }
    };

    loadSubtask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtaskId]); // Only depend on subtaskId to prevent infinite loops

  const handleModalClose = () => {
    setIsModalOpen(false);
    // Navigate to project page if parent task has project_id, otherwise go to dashboard
    if (parentTask?.project_id) {
      navigate(`/projects/${parentTask.project_id}/board`);
    } else if (subtask && (subtask as any).project_id) {
      navigate(`/projects/${(subtask as any).project_id}/board`);
    } else {
      navigate('/dashboard');
    }
  };

  const handleAddComment = async (taskId: string, comment: string, parentId?: string): Promise<Comment> => {
    const created = await commentAPI.createComment({
      task_id: taskId,
      content: comment,
      parent_comment_id: parentId,
    });
    if (subtaskId) {
      const data = await commentAPI.getComments(subtaskId);
      setComments(data || []);
      setActivity(data || []);
    }
    return created;
  };

  const handleDeleteComment = async (commentId: string) => {
    await deleteComment(commentId);
    if (subtaskId) {
      const data = await commentAPI.getComments(subtaskId);
      setComments(data || []);
      setActivity(data || []);
    }
  };

  const handleUpdateComment = async (commentId: string, content: string) => {
    await updateComment(commentId, content);
    if (subtaskId) {
      const data = await commentAPI.getComments(subtaskId);
      setComments(data || []);
      setActivity(data || []);
    }
  };

  const handleSave = async (updates: any) => {
    if (!subtaskId) return;
    try {
      await subtaskAPI.update(subtaskId, updates);
      const fresh = await subtaskAPI.get(subtaskId);
      setSubtask(fresh);
      toast.success('Subtask updated');
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to update subtask');
      throw err;
    }
  };

  const handleOpenParent = (parent: Task) => {
    // Navigate to parent task view
    navigate(`/task/${parent.id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader variant="overlay" size="lg" text="Loading subtask..." />
      </div>
    );
  }

  // Only show error if we actually failed to load the subtask
  // Don't show error if subtask loaded but parent task failed (that's non-critical)
  if (error && !subtask) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Subtask not found</h2>
          <p className="text-gray-600 mb-4">{error || 'The subtask you are looking for does not exist.'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to For You
          </button>
        </div>
      </div>
    );
  }

  // If we don't have a subtask and we're not loading, show error
  if (!subtask && !loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Subtask not found</h2>
          <p className="text-gray-600 mb-4">The subtask you are looking for does not exist.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to For You
          </button>
        </div>
      </div>
    );
  }

  return (
    <SubtaskModal
      subtask={subtask}
      isOpen={isModalOpen}
      onClose={handleModalClose}
      parentKey={parentTask ? `${projectKey}-${(parentTask as any)?.display_number ?? ''}`.replace(/-$/, '') : undefined}
      parentTitle={parentTask?.title}
      parentTask={parentTask as any}
      onOpenParent={handleOpenParent}
      projectMembers={projectMembers}
      activity={activity}
      projectKey={projectKey}
      onAddComment={handleAddComment}
      onUpdateComment={handleUpdateComment}
      onDeleteComment={handleDeleteComment}
      onSave={handleSave}
      onDelete={async () => {
        setIsModalOpen(false);
        navigate('/dashboard');
      }}
    />
  );
};

